// src/components/games/pipsWordPuzzle/PipsWordPuzzleMenu.tsx
import { logger } from '../../../utils/logger';
// FIXED: Removed emojis to prevent encoding corruption

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonBadge,
} from '@ionic/react';
import { calendar, statsChart, playCircle, timeOutline, arrowBack, trophy } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { isPuzzleCompleted, getLeaderboard, getPersonalBest, PipsWordPuzzleScore } from '../../../services/pipsWordPuzzleStorageService';
import { getDateString, getTodaysPuzzle } from './puzzles';
import { useIonViewWillEnter } from '@ionic/react';
import './PipsWordPuzzleMenu.css';

export const PipsWordPuzzleMenu: React.FC = () => {
  const history = useHistory();
  const [todayCompleted, setTodayCompleted] = useState(false);
  const [leaderboard, setLeaderboard] = useState<PipsWordPuzzleScore[]>([]);
  const [personalBest, setPersonalBest] = useState<number>(0);
  const [todayCategory, setTodayCategory] = useState<string>('');

  useEffect(() => {
    // Check if today's puzzle is completed and load category
    const loadTodayInfo = async () => {
      const today = getDateString();
      const completed = await isPuzzleCompleted(today);
      setTodayCompleted(completed);

      // Load today's puzzle to get the category
      try {
        const puzzle = await getTodaysPuzzle();
        setTodayCategory(puzzle.category || '');
      } catch (error) {
        logger.error('Failed to load puzzle category:', error);
      }
    };

    loadTodayInfo();
  }, []);

  // Load leaderboard data when view is entered
  useIonViewWillEnter(() => {
    const loadLeaderboardData = async () => {
      const [leaders, bestScore] = await Promise.all([
        getLeaderboard(10),
        getPersonalBest(),
      ]);
      setLeaderboard(leaders);
      setPersonalBest(bestScore);
    };

    loadLeaderboardData();
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.push('/tabs/games')}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Pip's Word Puzzle</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="pips-word-puzzle-menu-container">
          {/* Header/Logo */}
          <div className="pips-word-puzzle-menu-header">
            <div className="pips-word-puzzle-logo">🎮</div>
            <h1>Pip's Word Puzzle</h1>
            <p className="pips-word-puzzle-subtitle">Guess the gaming word!</p>
          </div>

          {/* Menu Options */}
          <div className="pips-word-puzzle-menu-options">
            {/* Daily Puzzle */}
            <IonCard
              className="menu-option-card daily-card"
              button
              onClick={() => history.push('/games/pips-word-puzzle/daily')}
            >
              <IonCardContent>
                <div className="menu-option-header">
                  <div className="menu-option-icon">
                    <IonIcon icon={calendar} style={{ fontSize: '64px' }} />
                  </div>
                  {todayCompleted && (
                    <IonBadge
                      color="success"
                      style={{
                        fontSize: '12px',
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        zIndex: 10
                      }}
                    >
                      ✓ Completed
                    </IonBadge>
                  )}
                </div>
                <h2>Daily Puzzle</h2>
                {todayCategory && (
                  <IonBadge color="medium" style={{ marginBottom: '8px', fontSize: '12px' }}>
                    {todayCategory}
                  </IonBadge>
                )}
                <p>{todayCompleted ? 'You\'ve completed today\'s puzzle!' : 'Play today\'s word challenge'}</p>
                <IonButton expand="block" color="primary">
                  <IonIcon icon={playCircle} slot="start" />
                  {todayCompleted ? 'View Today' : 'Play Today'}
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Archive */}
            <IonCard
              className="menu-option-card archive-card"
              button
              onClick={() => history.push('/games/pips-word-puzzle/archive')}
            >
              <IonCardContent>
                <div className="menu-option-icon">
                  <IonIcon icon={timeOutline} style={{ fontSize: '64px' }} />
                </div>
                <h2>Puzzle Archive</h2>
                <p>Play previous puzzles anytime</p>
                <IonButton expand="block" color="secondary">
                  Browse Archive
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Stats */}
            <IonCard
              className="menu-option-card stats-card"
              button
              onClick={() => history.push('/games/pips-word-puzzle/stats')}
            >
              <IonCardContent>
                <div className="menu-option-icon">
                  <IonIcon icon={statsChart} style={{ fontSize: '64px' }} />
                </div>
                <h2>Statistics</h2>
                <p>View your performance and achievements</p>
                <IonButton expand="block" color="tertiary">
                  View Stats
                </IonButton>
              </IonCardContent>
            </IonCard>
          </div>

          {/* Leaderboard Card */}
          <IonCard>
            <IonCardContent>
              <h2 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IonIcon icon={trophy} /> Top Players
              </h2>
              {personalBest > 0 && (
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(var(--ion-color-primary-rgb), 0.1)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '4px' }}>Your Personal Best</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--ion-color-primary)' }}>{personalBest} points</div>
                </div>
              )}
              {leaderboard.length > 0 ? (
                <div className="leaderboard-list">
                  {leaderboard.map((entry, index) => (
                    <div key={entry.id} className="leaderboard-entry">
                      <div className="rank-badge">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `#${index + 1}`}
                      </div>
                      <div className="player-info">
                        <div className="player-name">
                          {entry.display_name || entry.username || 'Anonymous'}
                        </div>
                        <div className="player-stats">
                          <span className="score">{entry.score.toLocaleString()} pts</span>
                          <span className="guesses">{entry.won ? `✓ ${entry.guesses_used} guesses` : '✗ Lost'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: 'var(--ion-color-medium)', margin: '20px 0' }}>
                  No scores yet. Be the first to set a record!
                </p>
              )}
            </IonCardContent>
          </IonCard>

          {/* How to Play */}
          <IonCard style={{ margin: '0', background: 'rgba(159, 122, 234, 0.05)', border: '1px solid rgba(159, 122, 234, 0.2)' }}>
            <IonCardContent>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: 'var(--ion-color-primary)' }}>
                ? How to Play
              </h3>
              <div style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--ion-color-medium)' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  • Guess the gaming word in 6 tries or less
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  • Green tiles mean the letter is correct and in the right spot
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  • Yellow tiles mean the letter is in the word but wrong spot
                </p>
                <p style={{ margin: '0' }}>
                  • Use hints (5 points each) if you get stuck!
                </p>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Footer Info */}
          <div className="pips-word-puzzle-menu-footer">
            <p>A new puzzle every day at midnight!</p>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};